'use client';

import type { schema } from '../zero-schema';
import type { clientMutators } from '../zero/client-mutators';
import { useState, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useZero } from '@rocicorp/zero/react';
import { runZeroMutation } from '../zero/run-mutation';
import { getOrganizationSwitchHref } from '@utils/route-utils';
import { useSearchCommand } from './use-search-command';

/** Shared by desktop and mobile; navigation waits for server authorization. */
export function useOrganizationSwitch(organizationId?: string) {
  const [switching, setSwitching] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations();
  const zero = useZero<typeof schema, typeof clientMutators>();
  const busy = useRef(false);
  const { setOpen } = useSearchCommand();

  async function switchOrganization(nextId: string) {
    if (busy.current || nextId === organizationId) return false;
    busy.current = true;
    setSwitching(true);
    try {
      const switched = await runZeroMutation(
        () =>
          zero.mutate.users.setDefaultOrganization({ organizationId: nextId }),
        { errorTitle: t('settings.organization-switch-error') }
      );
      if (!switched) return false;
      setOpen(false);
      router.replace(
        getOrganizationSwitchHref(pathname, organizationId, nextId)
      );
      router.refresh();
      return true;
    } finally {
      busy.current = false;
      setSwitching(false);
    }
  }

  return { switching, switchOrganization };
}
